import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/hooks/useReveal";
import { ArrowUpRight } from "lucide-react";
import { api, resolveImage } from "@/lib/api";

const Detail = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-[10px] tracking-[0.28em] uppercase text-black/40">{label}</span>
    <span className="text-sm tracking-wide text-black mt-1">{value || "—"}</span>
  </div>
);

const FeaturedListings = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Fetch all listings; featured acts as a sort priority, not a hard filter.
        // Anything Active / Coming Soon / Pending shows here; Sold goes into SoldListings.
        const { data } = await api.get("/listings", { params: { limit: 50 } });
        const visible = data
          .filter((l) => l.status !== "Sold")
          .sort((a, b) => {
            // Featured first, then preserve API order (newest first)
            if (!!b.featured - !!a.featured !== 0) return !!b.featured - !!a.featured;
            return 0;
          })
          .slice(0, 9);
        setListings(visible);
      } catch {
        setListings([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <section
      id="listings"
      data-testid="featured-listings-section"
      className="relative bg-white py-28 md:py-40 px-6 md:px-12"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
          <div>
            <Reveal>
              <p className="overline mb-6">Featured Listings</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-black">
                Currently
                <em className="italic font-light"> represented</em>.
              </h2>
            </Reveal>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-black/40">Loading...</p>
        ) : listings.length === 0 ? (
          <div className="border border-dashed border-black/10 px-8 py-24 text-center">
            <p className="overline mb-3">Portfolio in transition</p>
            <p className="text-black/50 max-w-md mx-auto">
              We're between published listings. Contact us for off-market opportunities.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-10 lg:gap-8">
            {listings.map((l, i) => (
              <Reveal key={l.id} delay={i * 100}>
                <Link
                  to={`/listings/${l.slug}`}
                  data-testid={`featured-listing-${l.id}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden aspect-[4/3] bg-[#f5f5f5]">
                    {l.image_urls?.[0] ? (
                      <img
                        src={resolveImage(l.image_urls[0])}
                        alt={l.address}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-[10px] uppercase tracking-[0.28em] text-black/30">
                        Photos coming soon
                      </div>
                    )}
                    <span className="absolute top-5 left-5 px-3 py-1 bg-white text-black text-[10px] tracking-[0.28em] uppercase font-medium">
                      {l.status}
                    </span>
                  </div>
                  <div className="pt-6 flex items-start justify-between">
                    <div>
                      <p className="font-heading text-3xl md:text-4xl tracking-tight text-black mb-1">
                        {l.price || "Inquire"}
                      </p>
                      <p className="text-sm text-black/70">{l.address}</p>
                      <p className="text-xs text-black/40 tracking-wider uppercase mt-1">
                        {[l.city, l.state, l.zip_code].filter(Boolean).join(", ")}
                      </p>
                    </div>
                    <ArrowUpRight
                      size={22}
                      className="text-black/40 group-hover:text-black group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
                    />
                  </div>
                  <div className="mt-6 pt-5 border-t border-black/10 grid grid-cols-3 gap-4">
                    <Detail label="Beds" value={l.beds} />
                    <Detail label="Baths" value={l.baths} />
                    <Detail label="Sq Ft" value={l.sqft} />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeaturedListings;
